// src/pages/Community/MissionsIndex.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
  getCountFromServer,
} from "firebase/firestore";
import { db } from "../../firebase";
import "./css/QuickPanel.css";
import "./css/MissionsIndex.css";
import ToolbarHeader from "./components/ToolbarHeader";
import { GTA_GAME_TABS } from "./components/ToolbarHeader";
import MissionForm from "./Missions/MissionForm"; // adjust path if needed
import Modal from "./components/Modal";
import { useLocation } from "react-router-dom";
import EmptyState from "./components/EmptyState";
import DetailSidePanel from "./components/DetailSidePanel";
import MissionDetailPanel from "./Missions/MissionDetailPanel";

export default function MissionsIndex({ user }) {
  // 1) state
  const [missions, setMissions] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter] = useState("main");
  const [commentCounts, setCommentCounts] = useState({});
  const [game, setGame] = useState("gta-5");
  const isSuperAdmin = user?.role === "superadmin";
  const isAdmin = isSuperAdmin || user?.role === "admin";
  const location = useLocation();
  const [activeMissionId, setActiveMissionId] = useState(null);

  // 2) helpers (keep here)
  const norm = (s) => (s || "").toString().trim().toLowerCase();
  const isMain = (m) => ["main", "main story", "story", "campaign"].includes(norm(m.category));
  const isSide = (m) => ["side", "side mission", "secondary"].includes(norm(m.category));
  const isMine = (m, user) => {
    const uid = user?.uid;
    return !!uid && [m.createdBy, m.userId, m.authorId, m.ownerId].some((x) => x === uid);
  };
  const navigate = useNavigate();

  // 3) list memo (👈 must be before any effect that uses `list`)
  const list = useMemo(() => {
    let arr = [...missions];

    // 👑 MY MISSIONS — absolute override
    if (filter === "mine") {
      return arr
        .filter((m) => isMine(m, user))
        .filter((m) => !game || game === "all" || m.gameKey === game);
    }

    // 🎮 Game filter (non-mine views only)
    if (game && game !== "all") {
      arr = arr.filter((m) => m.gameKey === game);
    }

    // =========================
    // 📂 TAB-SPECIFIC RULES
    // =========================

    // 🧑‍🎨 FAN MISSIONS TAB
    if (filter === "fan") {
      arr = arr.filter(
        (m) =>
          m.fanCreated === true &&
          m.isPrivate === false // 👈 public only
      );
    }

    // 📘 MAIN STORY (ADMIN ONLY)
    if (filter === "main") {
      arr = arr.filter(
        (m) =>
          m.fanCreated === false &&
          isMain(m)
      );
    }

    // 📙 SIDE MISSIONS (ADMIN ONLY)
    if (filter === "side") {
      arr = arr.filter(
        (m) =>
          m.fanCreated === false &&
          isSide(m)
      );
    }

    // 🔍 Search (applies last)
    if (search.trim()) {
      const q = search.toLowerCase();
      arr = arr.filter((m) =>
        [m.missionName, m.location, ...(m.tags || []), m.category]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(q)
      );
    }

    // 🔢 Sort
    arr.sort((a, b) => {
      const an = a.missionNumber ?? Infinity;
      const bn = b.missionNumber ?? Infinity;
      if (an !== bn) return an - bn;
      return (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0);
    });

    return arr;
  }, [missions, filter, game, search, user]);


  const missionsByGame = useMemo(() => {
    const groups = {};
    list.forEach((m) => {
      const key = m.gameKey || "unknown";
      if (!groups[key]) groups[key] = [];
      groups[key].push(m);
    });
    return groups;
  }, [list]);

  const buildMissionTree = (missions) => {
    const parents = [];
    const byParentId = {};

    missions.forEach((m) => {
      if (m.isVariant && m.parentMissionId) {
        if (!byParentId[m.parentMissionId]) {
          byParentId[m.parentMissionId] = [];
        }
        byParentId[m.parentMissionId].push(m);
      } else {
        parents.push(m);
      }
    });

    // sort variants (11.1, 11.2)
    Object.values(byParentId).forEach((arr) =>
      arr.sort((a, b) => (a.subMissionNumber ?? 0) - (b.subMissionNumber ?? 0))
    );

    // sort parents by mission number
    parents.sort(
      (a, b) => (a.missionNumber ?? 999) - (b.missionNumber ?? 999)
    );

    return parents.map((p) => ({
      parent: p,
      variants: byParentId[p.arcId] || [],
    }));
  };


  // 4) effects can now use `list` safely
  useEffect(() => {
    if (!list.length) {
      setCommentCounts({});
      return;
    }
    let cancelled = false;
    (async () => {
      const results = await Promise.all(
        list.map(async (m) => {
          try {
            const q = query(
              collection(db, "missions", m.id, "threads"),  // ⬅️ subcollection, not root
              where("type", "==", "comment")
            );
            const snap = await getCountFromServer(q);
            return [m.id, snap.data().count];
          } catch (e) {
            console.warn("count() failed for", m.id, e);
            return [m.id, undefined];
          }

        })
      );
      if (!cancelled) setCommentCounts(Object.fromEntries(results));
    })();
    return () => {
      cancelled = true;
    };
  }, [list]);


  // Load missions
  useEffect(() => {
    // helper to merge snapshots by id
    const mergeById = (...lists) => {
      const map = new Map();
      lists.flat().forEach((m) => map.set(m.id, m));
      // sort by createdAt desc (fallback 0)
      return [...map.values()].sort(
        (a, b) =>
          (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0)
      );
    };

    setLoading(true);

    // 1) Public missions
    const qPublic = query(
      collection(db, "missions"),
      where("isPrivate", "==", false),
      orderBy("createdAt", "desc")
    );

    let publicDocs = [];
    let mineDocs = [];
    const unsubs = [];

    const unsubPublic = onSnapshot(qPublic, (snap) => {
      publicDocs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setMissions((prev) => mergeById(publicDocs, mineDocs));
      setLoading(false);
    });
    unsubs.push(unsubPublic);

    // 2) My missions (may include private). Pick ONE authoritative owner field.
    // If your schema varies, prefer normalizing to `ownerId` on write.
    if (user?.uid) {
      // If you *must* support legacy fields, you can add extra listeners:
      // ownerId==uid OR createdBy==uid (FireStore doesn't support OR on fields)
      // so we listen to both and merge.
      const qs = [
        query(
          collection(db, "missions"),
          where("ownerId", "==", user.uid),
          orderBy("createdAt", "desc")
        ),
        query(
          collection(db, "missions"),
          where("createdBy", "==", user.uid),
          orderBy("createdAt", "desc")
        ),
      ];

      qs.forEach((qq) => {
        const u = onSnapshot(qq, (snap) => {
          // merge *these* "mine" results together first
          const batch = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          // merge into mineDocs by id (so ownerId & createdBy sets don’t duplicate)
          const tmp = new Map(mineDocs.map((m) => [m.id, m]));
          batch.forEach((m) => tmp.set(m.id, m));
          mineDocs = [...tmp.values()];

          // now merge with public and push to state
          setMissions(mergeById(publicDocs, mineDocs));
          setLoading(false);
        });
        unsubs.push(u);
      });
    }

    return () => unsubs.forEach((u) => u && u());
  }, [db, user?.uid]);

  useEffect(() => {
    if (location.state?.game) {
      setGame(location.state.game);
    }
    if (location.state?.filter) {
      setFilter(location.state.filter);
    }
  }, [location.state]);

  useEffect(() => {
    if (!activeMissionId) return;

    const onKey = (e) => {
      if (e.key === "Escape") setActiveMissionId(null);
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [activeMissionId]);

  return (
    <>
      <ToolbarHeader
        search={search}
        setSearch={setSearch}
        placeholder="Search missions, tags, location…"
        onAddMission={() => setShowModal(true)}
        user={user}
        addLabel="＋ Add Mission"
      >
        {/* 🎮 GAME FILTER (TEXT TABS) */}
        <div className="forum-tabs forum-tabs--games">
          {GTA_GAME_TABS.filter(g => g.key !== "all").map((g) => (
            <span
              key={g.key}
              className={`tab-text ${game === g.key ? "active" : ""}`}
              onClick={() => setGame(g.key)}
            >
              {g.label}
            </span>
          ))}
        </div>

        {/* 📂 CATEGORY FILTER */}
        <div className="forum-tabs">
          {["main", "side", "fan"].concat(user ? ["mine"] : [])
            .map((t) => (
              <span
                key={t}
                className={`tab-text ${filter === t ? "active" : ""}`}
                onClick={() => setFilter(t)}
              >
                {t === "main"
                  ? "Main Story"
                  : t === "side"
                    ? "Side Missions"
                    : t === "fan"
                      ? "Fan Missions"
                      : "My Missions"}

              </span>
            ))}
        </div>

      </ToolbarHeader>
      <div className={`msn-shell ${activeMissionId ? "panel-open" : ""}`}>
        <div className="msn-wrap">
          <ul className="msn-layout">
            {loading &&
              Array.from({ length: 6 }).map((_, i) => (
                <li key={`sk-${i}`} className="msn-card msn-card--skeleton">
                  <div className="msn-thumb skeleton" />
                  <div className="msn-card-body">
                    <div className="skeleton skeleton-line" />
                    <div className="skeleton skeleton-line short" />
                  </div>
                </li>
              ))}

            {!loading &&
              Object.entries(missionsByGame).map(([gameKey, missions]) => {
                const gameLabel =
                  GTA_GAME_TABS.find((g) => g.key === gameKey)?.label || "Other";

                const tree = buildMissionTree(missions);

                if (!tree.length) return null;

                return (
                  <React.Fragment key={gameKey}>
                    {/* 🎮 GAME HEADER */}
                    {/* <li className="msn-game-header">🎮 {gameLabel}</li> */}

                    {/* 🛡 ADMIN MISSIONS */}
                    {tree.map(({ parent, variants }) => (
                      <React.Fragment key={parent.id}>
                        {/* ===== PARENT ===== */}
                        <li
                          className="msn-admin-item"
                          onClick={() => setActiveMissionId(parent.id)}
                        >

                          <div className="msn-admin-row">
                            <div className="msn-admin-titleline">
                              {parent.missionNumber != null && (
                                <span className="msn-admin-num">
                                  #{parent.missionNumber}
                                </span>
                              )}

                              {parent.missionName}

                              {isMine(parent, user) && (
                                <span
                                  className="msn-owner-crown"
                                  title="Created by you"
                                >
                                  👑
                                </span>
                              )}
                            </div>


                            <div className="msn-admin-desc">
                              {parent.category} · {parent.location}
                              {parent.shortNote && ` — ${parent.shortNote}`}
                            </div>
                          </div>
                        </li>

                        {/* ===== VARIANTS ===== */}
                        {variants.map((v) => (
                          <li
                            key={v.id}
                            className="msn-admin-item"
                            onClick={() => setActiveMissionId(v.id)}
                          >
                            <div className="msn-admin-row">
                              <div className="msn-admin-titleline">
                                <span className="msn-admin-num msn-admin-num--sub">
                                  {v.displayNumber}
                                </span>

                                {v.missionName
                                  .replace(parent.missionName, "")
                                  .trim()}

                                {isMine(v, user) && (
                                  <span className="msn-owner-crown" title="Created by you">
                                    👑
                                  </span>
                                )}
                              </div>

                              <div className="msn-admin-desc">
                                {v.variantType?.toUpperCase()} · {v.location}
                                {v.shortNote && ` — ${v.shortNote}`}
                              </div>
                            </div>
                          </li>
                        ))}


                      </React.Fragment>
                    ))}
                  </React.Fragment>
                );
              })}

            {!loading && list.length === 0 && (
              <li className="msn-empty empty-block">
                <EmptyState
                  icon="🎯"
                  title="No Missions Available"
                  description="Please stay tuned. New missions will be added soon."
                />
              </li>
            )}
          </ul>
        </div>
        <DetailSidePanel
          open={!!activeMissionId}
          title="Mission Details"
          onClose={() => setActiveMissionId(null)}
          onMaximize={() => navigate(`/missions/${activeMissionId}`)}
        >
          <MissionDetailPanel missionId={activeMissionId} user={user} />
        </DetailSidePanel>
      </div>

      {/* Reusable Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title="Submit a Mission"
        size="md" // "sm" | "md" | "lg" | number (percentage)
        closeOnBackdrop={true}
        showClose={true}
      >
        <MissionForm
          user={user}
          enforceAdmin={false}
          isAdmin={isAdmin}
          isSuperAdmin={isSuperAdmin}
          onSaved={() => setShowModal(false)}
          onCancel={() => setShowModal(false)}
          initial={{ difficulty: "Normal" }}
          mode="create"
        />

      </Modal>



    </>
  );
}
